import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Chip, TextField, InputAdornment, Select, MenuItem,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import InboxIcon from "@mui/icons-material/Inbox";
import { Colors } from "../../Theme/Theme";
import { Forms, WEBSITE_INQUIRY_TYPE } from "../../api/requests/Forms";
import { handleAlert } from "../../utils/handleAlert";

const STATUSES = [
  { value: "new", label: "New", color: "#2563EB", bg: "#EFF6FF" },
  { value: "contacted", label: "Contacted", color: "#D97706", bg: "#FFFBEB" },
  { value: "converted", label: "Converted", color: "#059669", bg: "#ECFDF5" },
  { value: "closed", label: "Closed", color: "#64748B", bg: "#F1F5F9" },
];
const statusOf = (value) => STATUSES.find((s) => s.value === value) || STATUSES[0];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fullName = (row) =>
  row.userinfo_meta?.full_name || [row.first_name, row.last_name].filter(Boolean).join(" ") || "-";

const whatsappLink = (row) => {
  const text = `Hello ${fullName(row).split(" ")[0]}, this is Fi Pay Bills regarding your enquiry${
    row.form_meta?.ticket_id ? ` (${row.form_meta.ticket_id})` : ""
  } about "${row.userinfo_meta?.solution || "our services"}".`;
  return `https://wa.me/91${row.phone_number}?text=${encodeURIComponent(text)}`;
};

const sourceLabel = (meta = {}) => {
  if (meta.gclid) return "Google Ads";
  if (meta.fbclid) return "Facebook Ads";
  if (meta.utm_source) return meta.utm_source;
  return meta.source === "ad_landing" ? "Ad page" : "Website";
};

const headCellSx = {
  fontWeight: 600, fontSize: "11px", color: Colors.secondaryColor, textTransform: "uppercase",
  letterSpacing: "0.5px", py: 1.5, backgroundColor: "#F9FBFC",
  borderBottom: `1px solid ${Colors.dividerColor}`, whiteSpace: "nowrap",
};
const cellSx = { fontSize: "13px", color: Colors.primaryTextColor, borderBottom: `1px solid ${Colors.dividerColor}` };

const StatusSelect = ({ value, onChange, disabled }) => {
  const s = statusOf(value);
  return (
    <Select
      size="small"
      value={s.value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      sx={{
        fontSize: "12px", fontWeight: 600, color: s.color, backgroundColor: s.bg, borderRadius: "8px", minWidth: 120,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
        "& .MuiSelect-select": { py: 0.6 },
      }}
    >
      {STATUSES.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>{opt.label}</MenuItem>
      ))}
    </Select>
  );
};

const DetailRow = ({ label, children }) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.75 }}>
    <Typography sx={{ width: 130, flexShrink: 0, fontSize: "13px", color: Colors.secondaryColor }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: "13px", color: Colors.primaryTextColor, fontWeight: 500, wordBreak: "break-word" }}>
      {children || "-"}
    </Typography>
  </Box>
);

const WebsiteInquiries = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        ["", ...STATUSES.map((s) => s.value)].map((status) =>
          Forms.getForms({ page: 1, limit: 1, type: WEBSITE_INQUIRY_TYPE, status })
        )
      );
      const next = {};
      ["all", ...STATUSES.map((s) => s.value)].forEach((key, i) => {
        next[key] = Number(results[i]?.pagination?.total) || 0;
      });
      setCounts(next);
    } catch {
      // counts are informational only
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await Forms.getForms({
        page: page + 1, limit: rowsPerPage, type: WEBSITE_INQUIRY_TYPE, search, status: statusFilter,
      });
      setRows(res?.data || []);
      setTotalRows(Number(res?.pagination?.total) || 0);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const refresh = () => { fetchRows(); fetchCounts(); };

  const updateStatus = async (row, status) => {
    setUpdatingId(row.id);
    try {
      const res = await Forms.updateFormStatus(row.id, status);
      const updated = res?.data;
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      if (selected?.id === row.id) setSelected(updated);
      fetchCounts();
      handleAlert(`Marked as ${statusOf(status).label}`, "success");
    } catch (err) {
      handleAlert(err?.response?.data?.message || "Could not update status", "error");
    } finally {
      setUpdatingId(null);
    }
  };

  const filterChips = [{ value: "", label: "All", key: "all" }, ...STATUSES.map((s) => ({ ...s, key: s.value }))];

  return (
    <>
      {/* Status filter + search */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {filterChips.map((chip) => {
            const active = statusFilter === chip.value;
            return (
              <Chip
                key={chip.key}
                clickable
                onClick={() => { setStatusFilter(chip.value); setPage(0); }}
                label={`${chip.label}${counts[chip.key] !== undefined ? ` · ${counts[chip.key]}` : ""}`}
                sx={{
                  fontWeight: 600, fontSize: "12px", borderRadius: "8px", height: 30,
                  backgroundColor: active ? Colors.primaryTextColor : Colors.white,
                  color: active ? Colors.white : Colors.primaryTextColor,
                  border: `1px solid ${active ? Colors.primaryTextColor : Colors.dividerColor}`,
                  "&:hover": { backgroundColor: active ? Colors.primaryTextColor : Colors.blueSoft },
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
          <TextField
            size="small"
            placeholder="Search name, mobile, company, ref…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: { sm: 280 }, flex: 1, backgroundColor: Colors.white, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} sx={{ border: `1px solid ${Colors.dividerColor}`, borderRadius: "8px", backgroundColor: Colors.white }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: Colors.primary }} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: Colors.error, fontSize: "14px", mb: 1 }}>{error}</Typography>
            <Button size="small" onClick={refresh}>Try again</Button>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <InboxIcon sx={{ fontSize: 40, color: Colors.dividerColor, mb: 1 }} />
            <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px" }}>
              {search || statusFilter ? "No enquiries match these filters" : "No website enquiries yet"}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto", opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Received", "Reference", "Name", "Mobile", "Company / City", "Interested in", "Source", "Status", ""].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => setSelected(row)}
                      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#FAFBFF" } }}
                    >
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap", color: Colors.secondaryColor, fontSize: "12px" }}>
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontFamily: "monospace", fontSize: "12px", whiteSpace: "nowrap" }}>
                        {row.form_meta?.ticket_id || "-"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {fullName(row)}
                        {row.email && (
                          <Typography sx={{ fontSize: "11px", color: Colors.secondaryColor }}>{row.email}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap", fontFamily: "monospace" }}>
                        +91 {row.phone_number}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                        <Typography noWrap sx={{ fontSize: "13px" }}>{row.userinfo_meta?.company_name || "-"}</Typography>
                        {row.userinfo_meta?.city && (
                          <Typography noWrap sx={{ fontSize: "11px", color: Colors.secondaryColor }}>{row.userinfo_meta.city}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 240 }}>
                        <Typography noWrap sx={{ fontSize: "13px", color: Colors.primary, fontWeight: 500 }}>
                          {row.userinfo_meta?.solution || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 160 }}>
                        <Typography noWrap sx={{ fontSize: "12px", fontWeight: 600 }}>{sourceLabel(row.form_meta)}</Typography>
                        {row.form_meta?.utm_campaign && (
                          <Typography noWrap sx={{ fontSize: "11px", color: Colors.secondaryColor }}>{row.form_meta.utm_campaign}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <StatusSelect
                          value={row.form_meta?.status}
                          disabled={updatingId === row.id}
                          onChange={(status) => updateStatus(row, status)}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Call">
                          <IconButton size="small" href={`tel:+91${row.phone_number}`}><PhoneIcon sx={{ fontSize: 18, color: Colors.primary }} /></IconButton>
                        </Tooltip>
                        <Tooltip title="WhatsApp">
                          <IconButton size="small" href={whatsappLink(row)} target="_blank" rel="noopener noreferrer">
                            <WhatsAppIcon sx={{ fontSize: 18, color: "#059669" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setSelected(row)}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: `1px solid ${Colors.dividerColor}`, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "12px", color: Colors.secondaryColor } }}
            />
          </>
        )}
      </Box>

      {/* Details dialog */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        {selected && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography sx={{ fontSize: "12px", color: Colors.secondaryColor, fontFamily: "monospace" }}>
                {selected.form_meta?.ticket_id || "Website enquiry"} · {formatDate(selected.created_at)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: Colors.primaryTextColor }}>{fullName(selected)}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <DetailRow label="Status">
                <StatusSelect
                  value={selected.form_meta?.status}
                  disabled={updatingId === selected.id}
                  onChange={(status) => updateStatus(selected, status)}
                />
              </DetailRow>
              <DetailRow label="Mobile">+91 {selected.phone_number}</DetailRow>
              <DetailRow label="Email">{selected.email}</DetailRow>
              <DetailRow label="Company">{selected.userinfo_meta?.company_name}</DetailRow>
              <DetailRow label="City / pincode">{selected.userinfo_meta?.city}</DetailRow>
              <DetailRow label="Interested in">{selected.userinfo_meta?.solution}</DetailRow>
              <DetailRow label="Message">
                <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>{selected.userinfo_meta?.message}</Box>
              </DetailRow>
              <Divider sx={{ my: 1 }} />
              <DetailRow label="Source">{sourceLabel(selected.form_meta)}{selected.form_meta?.page ? ` (${selected.form_meta.page})` : ""}</DetailRow>
              {["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].map((k) =>
                selected.form_meta?.[k] ? <DetailRow key={k} label={k.replace("utm_", "Ad ")}>{selected.form_meta[k]}</DetailRow> : null
              )}
              {selected.form_meta?.status_updated_at && (
                <DetailRow label="Last updated">
                  {formatDate(selected.form_meta.status_updated_at)}
                  {selected.form_meta.status_updated_by ? ` by ${selected.form_meta.status_updated_by}` : ""}
                </DetailRow>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => setSelected(null)} sx={{ color: Colors.secondaryColor, textTransform: "none" }}>Close</Button>
              <Button variant="outlined" startIcon={<PhoneIcon />} href={`tel:+91${selected.phone_number}`} sx={{ textTransform: "none", borderRadius: "8px" }}>
                Call
              </Button>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                href={whatsappLink(selected)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "#059669", "&:hover": { backgroundColor: "#047857" } }}
              >
                WhatsApp
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default WebsiteInquiries;
